import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  IconButton,
  Tooltip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SyncIcon from '@mui/icons-material/Sync';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import CustomCard from '../../components/UIComponents/CustomCard';
import BasicTable from '../../components/Table';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/auth.context';
import { useLoading } from "../../context/loading.context";
import moment from 'moment';
import { getDevices } from '../../service/network';

const StatusBadge = ({ status }) => {
  const statusConfig = {
    'Active': {
      icon: <CheckCircleIcon fontSize="small" />,
      bgColor: '#e8f5e9',
      textColor: '#4caf50',
      label: 'Active'
    },
    'Inactive': {
      icon: <CancelIcon fontSize="small" />,
      bgColor: '#ffebee',
      textColor: '#f44336',
      label: 'Inactive'
    }
  };

  const config = statusConfig[status] || {
    icon: <CancelIcon fontSize="small" />,
    bgColor: '#ffebee',
    textColor: '#f44336',
    label: 'Inactive'
  };

  return (
    <Chip
      icon={config.icon}
      label={config.label}
      sx={{
        backgroundColor: config.bgColor,
        color: config.textColor,
        fontWeight: 600,
        minWidth: '100px',
        '.MuiChip-icon': {
          color: config.textColor,
          marginLeft: '4px'
        }
      }}
      size="small"
    />
  );
};

// Generate dummy device data
const generateDummyDevices = () => {
  const statuses = ['Active', 'Inactive'];
  const locations = ['Main Office', 'Warehouse', 'Factory', 'Remote'];
  const devices = [];

  for (let i = 1; i <= 20; i++) {
    const randomStatus = statuses[Math.floor(Math.random() * statuses.length)];
    const randomLocation = locations[Math.floor(Math.random() * locations.length)];
    const randomDays = Math.floor(Math.random() * 30);

    devices.push({
      id: `DEV-${1000 + i}`,
      name: `Device ${i}`,
      location: randomLocation,
      status: randomStatus,
      lastSync: moment().subtract(randomDays, 'days').format('YYYY-MM-DD HH:mm:ss'),
      ipAddress: `192.168.1.${i}`,
      macAddress: `00:1A:2B:3C:4D:${i.toString(16).padStart(2, '0')}`
    });
  }

  return devices;
};

export default function DeviceManagement() {
  const [devices, setDevices] = useState([]);
  const [filteredDevices, setFilteredDevices] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const { showLoader, hideLoader } = useLoading();
  const navigate = useNavigate();

  const deviceTableConfig = {
    rows: [
      {
        id: 'name',
        label: 'Device Name',
        flex: 1.5,
        minWidth: '180px',
        accessor: 'name',
        align: 'left',
        canSort: true
      },
      {
        id: 'location',
        label: 'Location',
        flex: 1.2,
        minWidth: '150px',
        accessor: 'location',
        align: 'left',
        canSort: true
      },
      {
        id: 'status',
        label: 'Status',
        flex: 1,
        minWidth: '120px',
        accessor: 'status',
        align: 'center',
        canSort: true,
        render: (row) => <StatusBadge status={row.status} />
      },
      {
        id: 'lastSync',
        label: 'Last Sync',
        flex: 1.2,
        minWidth: '180px',
        accessor: 'lastSync',
        align: 'center',
        canSort: true,
        render: (row) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SyncIcon fontSize="small" color="action" />
            {moment(row.lastSync).format('DD MMM YYYY, hh:mm A')}
          </Box>
        )
      }
    ]
  };


  useEffect(() => {
    // Apply filters whenever search term or status filter changes
    let result = devices;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(device =>
        device.name.toLowerCase().includes(term) ||
        device.location.toLowerCase().includes(term)
      );
    }

    if (statusFilter !== 'All') {
      result = result.filter(device => device.status === statusFilter);
    }

    setFilteredDevices(result);
  }, [searchTerm, statusFilter, devices]);

  const handleDeviceAction = (deviceId) => {
    // navigate(`/devices/${deviceId}`);
  };

  useEffect(() => {
    showLoader();
    getDevices().then((resp) => {
      let temp = [];
      for (let dvc of resp.data) {
        temp.push({
          ...dvc,
          status: moment(dvc.LastSeen).isBefore(moment().subtract(2, 'hours')) ? "Inactive" : "Active",
          name: dvc.DeviceID,
          location: dvc.Location,
          lastSync: moment(dvc.LastSeen).format('YYYY-MM-DD HH:mm:ss'),
        });
      }
      setDevices(temp);
    }).catch((err) => {
      console.log(err);
    }).finally(() => {
      hideLoader();
    })
  }, []);

  const handleRefresh = () => {

  };

  const handleSyncAll = () => {

  };

  return (
    <Box sx={{ width: '100%', p: { xs: 1, sm: 2 }, boxSizing: 'border-box' }}>
      <CustomCard
        bgColor="light"
        shadow="lg"
        borderRadius="xxl"
        sx={{
          width: '100%',
          minHeight: 800,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          p: 3
        }}
      >
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Box>
                        <Typography variant="subTitle" gutterBottom color='primary.main'>
              Device Management
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Monitor and manage all connected devices
            </Typography>
          </Box>
        </Box>

        {/* Filter Section */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Search devices..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                )
              }}
            />
          </Grid>


        </Box>

        <Box sx={{ flex: 1 }}>
          <BasicTable
            data={{
              ...deviceTableConfig,
              columns: filteredDevices
            }}
            backgroundColor="#f9fafc"
          />
        </Box>
      </CustomCard>
    </Box>
  );
}